import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Icon from '../../components/AppIcon';
import VideoGrid from './components/VideoGrid';
import ChatPanel from './components/ChatPanel';
import ParticipantsList from './components/ParticipantsList';
import ParticipantsCard from './components/ParticipantsCard';
import ControlBar from './components/ControlBar';
import SessionInfo from './components/SessionInfo';
import PollWidget from './components/PollWidget';
import SharedFiles from './components/SharedFiles';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import {
  addChildToMeeting,
  closeMeetingPoll,
  createMeetingPoll,
  ensureMeetingChat,
  fetchActiveMeetingPoll,
  fetchMeeting,
  fetchMeetingFiles,
  fetchMyChildrenForMeeting,
  uploadMeetingFile,
  voteMeetingPoll,
  type MeetingEvent,
  type MeetingFileItem,
  type MeetingPoll,
  type MyChildOption,
} from '../../lib/meetingApi';
import { wsUrl } from '../../lib/wsUrl';
import { isSubscriptionError } from '../../lib/subscriptionGate';
import type { ChatMessage, ClassSession, Participant, Poll, SharedFile } from './types';

function buildIceServers(): RTCIceServer[] {
  const servers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ];

  const turnUrlsRaw = (import.meta.env.VITE_TURN_URLS || import.meta.env.VITE_TURN_URL || '').toString().trim();
  if (turnUrlsRaw) {
    const username = (import.meta.env.VITE_TURN_USERNAME || '').toString();
    const credential = (import.meta.env.VITE_TURN_CREDENTIAL || '').toString();
    const urls = turnUrlsRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    for (const u of urls) {
      servers.push({
        urls: u,
        username: username || undefined,
        credential: credential || undefined,
      });
    }
  }

  return servers;
}

type ConversationMessage = {
  id: number;
  conversation_id: string;
  sender_id: number;
  sender_name: string;
  sender_role: string;
  sender_avatar_url?: string | null;
  content: string;
  timestamp: string;
};

type SignalMessage =
  | { type: 'peers'; peers: number[]; you: number }
  | { type: 'peer-joined'; peer_id: number }
  | { type: 'peer-left'; peer_id: number }
  | { type: 'offer'; to: number; from: number; sdp: RTCSessionDescriptionInit }
  | { type: 'answer'; to: number; from: number; sdp: RTCSessionDescriptionInit }
  | { type: 'ice-candidate'; to: number; from: number; candidate: RTCIceCandidateInit }
  | { type: 'hand'; from: number; raised?: boolean };

function roleToUiRole(role: string | null | undefined): Participant['role'] {
  const r = (role || '').trim().toLowerCase();
  if (r === 'admin') return 'admin';
  if (r === 'profesor' || r === 'teacher') return 'teacher';
  if (r === 'parinte' || r === 'parent') return 'parent';
  return 'student';
}

function fmtDateTimeRo(d: Date): string {
  return d.toLocaleString('ro-RO', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const OnlineClassroom: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [searchParams] = useSearchParams();
  const { me, uiRole, signOut } = useAuth();

  const isMobileUa = useMemo(() => {
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      return /Android|iPhone|iPad|iPod/i.test(ua);
    } catch {
      return false;
    }
  }, []);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);

  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  // Desktop browsers usually allow autoplay; mobile webviews often require a user gesture to unmute playback.
  const [playbackEnabled, setPlaybackEnabled] = useState(() => !isMobileUa);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [handRaisedByUserId, setHandRaisedByUserId] = useState<Record<string, boolean>>({});

  const [meeting, setMeeting] = useState<MeetingEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [myChildren, setMyChildren] = useState<MyChildOption[]>([]);
  const [myChildrenLoading, setMyChildrenLoading] = useState(false);
  const [myChildrenError, setMyChildrenError] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [addChildBusy, setAddChildBusy] = useState(false);
  const [addChildInfo, setAddChildInfo] = useState<string | null>(null);

  const [activePoll, setActivePoll] = useState<Poll | null>(null);
  const [hasVotedInPoll, setHasVotedInPoll] = useState(false);

  const [sharedFiles, setSharedFiles] = useState<SharedFile[]>([]);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const eventId = useMemo(() => {
    const fromQuery = searchParams.get('event');
    const fromState = location?.state?.eventId ?? location?.state?.event_id ?? null;
    const raw = fromQuery ?? fromState ?? null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  }, [location?.state?.eventId, location?.state?.event_id, searchParams]);

  const currentUser = useMemo(() => {
    const name = me?.full_name || 'Utilizator';
    const role = (uiRole || roleToUiRole(me?.role)) as Participant['role'];
    return {
      id: String(me?.id ?? ''),
      name,
      role,
      avatar: me?.avatar_url ?? undefined,
    };
  }, [me?.avatar_url, me?.full_name, me?.id, me?.role, uiRole]);

  const session: ClassSession | null = useMemo(() => {
    if (!meeting) return null;
    const start = new Date(meeting.start_time);
    const end = new Date(meeting.end_time);
    return {
      id: String(meeting.id),
      title: meeting.title,
      subject: meeting.event_type || 'Clasă online',
      startTime: start,
      endTime: end,
      isRecording,
    };
  }, [isRecording, meeting]);

  const canModerate = currentUser.role === 'teacher' || currentUser.role === 'admin';
  const screenShareAvailable = useMemo(() => {
    try {
      const mediaDevices: any = typeof navigator !== 'undefined' ? (navigator as any).mediaDevices : null;
      if (!mediaDevices?.getDisplayMedia) return false;
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      return !/Android|iPhone|iPad|iPod/i.test(ua);
    } catch {
      return false;
    }
  }, []);
  const recordingAvailable = false;
  const settingsAvailable = false;
  const participantsCardRef = useRef<HTMLDivElement | null>(null);
  const chatInlineRef = useRef<HTMLDivElement | null>(null);

  // media / rtc state
  const wsRef = useRef<WebSocket | null>(null);
  const pcsRef = useRef<Map<number, RTCPeerConnection>>(new Map());
  const remoteStreamsRef = useRef<Map<number, MediaStream>>(new Map());
  const [remoteStreamsState, setRemoteStreamsState] = useState<Record<string, MediaStream>>({});

  const cameraStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const [localPreviewStream, setLocalPreviewStream] = useState<MediaStream | null>(null);
  const myUserIdNum = useMemo(() => Number(me?.id ?? NaN), [me?.id]);

  const uiParticipants: Participant[] = useMemo(() => {
    if (!meeting) return [];
    const creatorId = meeting.created_by_id ?? null;

    const mapped = (meeting.participants || []).map((p) => {
      const pid = String(p.user_id);
      const isMe = pid === String(me?.id ?? '');
      const stream = isMe ? localPreviewStream : remoteStreamsState[pid] ?? null;
      const hasVideoTrack = !!stream?.getVideoTracks().length;
      const hasAudioTrack = !!stream?.getAudioTracks().length;

      return {
        id: pid,
        name: p.full_name || p.email || `User ${p.user_id}`,
        role: roleToUiRole(p.role),
        avatar: p.avatar_url ?? undefined,
        isVideoOn: isMe ? isVideoOn : hasVideoTrack,
        isAudioOn: isMe ? isAudioOn : hasAudioTrack,
        isHandRaised: isMe ? isHandRaised : Boolean(handRaisedByUserId[pid]),
        isPresenting: creatorId !== null ? Number(p.user_id) === Number(creatorId) : false,
        stream,
      };
    });

    // ensure the creator exists in UI list even if not in participants (back-compat)
    if (creatorId && !mapped.some((p) => Number(p.id) === Number(creatorId))) {
      const pid = String(creatorId);
      mapped.unshift({
        id: pid,
        name: 'Creator',
        role: 'teacher',
        isVideoOn: false,
        isAudioOn: false,
        isHandRaised: Boolean(handRaisedByUserId[pid]),
        isPresenting: true,
        stream: remoteStreamsState[pid] ?? null,
      });
    }

    // ensure current user appears
    if (me?.id && !mapped.some((p) => p.id === String(me.id))) {
      mapped.unshift({
        id: String(me.id),
        name: currentUser.name,
        role: currentUser.role,
        avatar: currentUser.avatar,
        isVideoOn,
        isAudioOn,
        isHandRaised,
        isPresenting: false,
        stream: localPreviewStream,
      });
    }

    return mapped;
  }, [
    currentUser.avatar,
    currentUser.name,
    currentUser.role,
    handRaisedByUserId,
    isAudioOn,
    isHandRaised,
    isVideoOn,
    localPreviewStream,
    me?.id,
    meeting,
    remoteStreamsState,
  ]);

  const mainPresenter = useMemo(() => {
    return uiParticipants.find((p) => p.isPresenting) || uiParticipants[0] || null;
  }, [uiParticipants]);

  const handleNavigation = (path: string) => navigate(path);

  const handleLeaveClass = () => {
    navigate('/calendar');
  };

  const openChatInline = () => {
    setPlaybackEnabled(true);
    setIsChatOpen(true);
  };

  useEffect(() => {
    if (!isChatOpen) return;
    if (isSidebarCollapsed) return;
    const t = window.setTimeout(() => {
      chatInlineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [isChatOpen, isSidebarCollapsed]);

  // sidebar responsive
  useEffect(() => {
    const handleResize = () => setIsSidebarCollapsed(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // load meeting + chat
  useEffect(() => {
    if (!me || !eventId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const m = await fetchMeeting(eventId);
        setMeeting(m);
        const chat = await ensureMeetingChat(eventId);
        setConversationId(chat.conversation_id);
      } catch (e: any) {
        setMeeting(null);
        setConversationId(null);
        setError(e?.response?.data?.detail || 'Nu am putut încărca sesiunea.');
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, me]);

  const mapPoll = (p: MeetingPoll): Poll => ({
    id: String(p.id),
    question: p.question,
    options: (p.options || []).map((o) => ({ id: String(o.id), text: o.text, votes: o.votes })),
    isActive: Boolean(p.is_active),
    totalVotes: Number(p.total_votes || 0),
  });

  const refreshPoll = async () => {
    if (!eventId || !me) return;
    try {
      const p = await fetchActiveMeetingPoll(eventId);
      if (!p) {
        setActivePoll(null);
        setHasVotedInPoll(false);
        return;
      }
      setActivePoll(mapPoll(p));
      setHasVotedInPoll(Boolean(p.my_vote_option_id));
    } catch (e) {
      // ignore
    }
  };

  // Keep poll updated for all participants (no WS for poll events yet).
  useEffect(() => {
    if (!me || !eventId) return;
    void refreshPoll();
    const t = window.setInterval(() => void refreshPoll(), 3000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshPoll();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, me?.id]);

  const mapFile = (f: MeetingFileItem): SharedFile => ({
    id: String(f.id),
    name: f.name,
    type: f.type,
    size: f.size_label,
    uploadedBy: f.uploaded_by || '—',
    uploadedAt: new Date(f.uploaded_at),
    url: f.url,
  });

  const refreshFiles = async () => {
    if (!eventId || !me) return;
    setFilesError(null);
    try {
      const list = await fetchMeetingFiles(eventId);
      setSharedFiles(list.map(mapFile));
    } catch (e: any) {
      setSharedFiles([]);
      setFilesError(e?.response?.data?.detail || 'Nu am putut încărca fișierele.');
    }
  };

  useEffect(() => {
    if (!me || !eventId) return;
    void refreshPoll();
    void refreshFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, me?.id]);

  // load parent's children options
  useEffect(() => {
    if (!me || !eventId) return;
    if (currentUser.role !== 'parent') return;
    setMyChildrenLoading(true);
    setMyChildrenError(null);
    (async () => {
      try {
        const kids = await fetchMyChildrenForMeeting(eventId);
        setMyChildren(kids);
        setSelectedChildId((prev) => prev ?? kids[0]?.id ?? null);
      } catch (e: any) {
        setMyChildren([]);
        setMyChildrenError(e?.response?.data?.detail || 'Nu am putut încărca lista de copii.');
      } finally {
        setMyChildrenLoading(false);
      }
    })();
  }, [currentUser.role, eventId, me]);

  // load messages when conversation exists
  const refreshChatMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const resp = await api.get<ConversationMessage[]>(`/api/users/conversations/${conversationId}/messages`);
      const mapped = resp.data.map((m) => ({
        id: String(m.id),
        senderId: String(m.sender_id),
        senderName: m.sender_name,
        senderRole: roleToUiRole(m.sender_role),
        message: m.content,
        timestamp: new Date(m.timestamp),
        isPrivate: false,
      }));
      setMessages((prev) => {
        const prevLast = prev.length ? String(prev[prev.length - 1]?.id) : null;
        const nextLast = mapped.length ? String(mapped[mapped.length - 1]?.id) : null;
        if (prevLast && nextLast && prevLast === nextLast && prev.length === mapped.length) return prev;
        return mapped;
      });
    } catch {
      // Keep last known messages; retry via polling.
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    void refreshChatMessages();
    const t = window.setInterval(() => void refreshChatMessages(), 3000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshChatMessages();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [conversationId, refreshChatMessages]);

  const handleSendMessage = async (message: string) => {
    if (!conversationId) return;
    const trimmed = message.trim();
    if (!trimmed) return;

    try {
      const resp = await api.post<ConversationMessage>(`/api/users/conversations/${conversationId}/messages`, {
        content: trimmed,
      });
      const m = resp.data;
      setMessages((prev) => [
        ...prev,
        {
          id: String(m.id),
          senderId: String(m.sender_id),
          senderName: m.sender_name,
          senderRole: roleToUiRole(m.sender_role),
          message: m.content,
          timestamp: new Date(m.timestamp),
          isPrivate: false,
        },
      ]);
      // Ensure we pick up any concurrent messages from other peers.
      void refreshChatMessages();
      return true;
    } catch (e) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return false;
      }
      const detail = (e as any)?.response?.data?.detail;
      window.alert(detail || 'Nu am putut trimite mesajul. Incearca din nou.');
      return false;
    }
  };

  const closePeer = (peerId: number) => {
    const pc = pcsRef.current.get(peerId);
    if (pc) {
      try {
        pc.onicecandidate = null;
        pc.ontrack = null;
        pc.close();
      } catch (e) {
        // ignore
      }
      pcsRef.current.delete(peerId);
    }
    remoteStreamsRef.current.delete(peerId);
    setHandRaisedByUserId((prev) => {
      const key = String(peerId);
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setRemoteStreamsState((prev) => {
      const next = { ...prev };
      delete next[String(peerId)];
      return next;
    });
  };

  const sendSignal = (payload: any) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  };

  const ensurePeerConnection = (peerId: number): RTCPeerConnection => {
    const existing = pcsRef.current.get(peerId);
    if (existing) return existing;

    const pc = new RTCPeerConnection({
      iceServers: buildIceServers(),
    });

    const cam = cameraStreamRef.current;
    if (cam) {
      for (const track of cam.getTracks()) pc.addTrack(track, cam);
      // If we start with mic muted (mobile), keep an audio m-line in SDP so we can attach the mic later
      // without requiring a renegotiation.
      try {
        if ((cam.getAudioTracks?.() || []).length === 0) {
          pc.addTransceiver('audio', { direction: 'sendrecv' });
        }
      } catch {
        // ignore
      }
    }

    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      const c = ev.candidate;
      // Android WebView / some browsers may not implement RTCIceCandidate.toJSON().
      sendSignal({
        type: 'ice-candidate',
        to: peerId,
        candidate: {
          candidate: c.candidate,
          sdpMid: c.sdpMid,
          sdpMLineIndex: c.sdpMLineIndex,
          usernameFragment: (c as any).usernameFragment,
        },
      });
    };

    pc.ontrack = (ev) => {
      const stream = ev.streams?.[0] || new MediaStream([ev.track]);
      remoteStreamsRef.current.set(peerId, stream);
      setRemoteStreamsState((prev) => ({ ...prev, [String(peerId)]: stream }));
    };

    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'failed' || st === 'closed' || st === 'disconnected') {
        closePeer(peerId);
      }
    };

    pcsRef.current.set(peerId, pc);
    return pc;
  };

  const makeOffer = async (peerId: number) => {
    const pc = ensurePeerConnection(peerId);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sendSignal({ type: 'offer', to: peerId, sdp: offer });
  };

  const handleSignal = async (msg: SignalMessage) => {
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'peers') {
      const peers = msg.peers || [];
      // deterministic: higher id initiates to avoid glare
      for (const peerId of peers) {
        if (Number.isFinite(myUserIdNum) && Number(myUserIdNum) > Number(peerId)) {
          void makeOffer(peerId);
        }
      }
      return;
    }
    if (msg.type === 'peer-joined') {
      const peerId = msg.peer_id;
      if (Number.isFinite(myUserIdNum) && Number(myUserIdNum) > Number(peerId)) {
        void makeOffer(peerId);
      }
      return;
    }
    if (msg.type === 'peer-left') {
      closePeer(msg.peer_id);
      return;
    }
    if (msg.type === 'hand') {
      const peerId = msg.from;
      if (peerId != null) {
        setHandRaisedByUserId((prev) => ({ ...prev, [String(peerId)]: Boolean(msg.raised) }));
      }
      return;
    }
    if (msg.type === 'offer') {
      const peerId = msg.from;
      const pc = ensurePeerConnection(peerId);
      await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignal({ type: 'answer', to: peerId, sdp: answer });
      return;
    }
    if (msg.type === 'answer') {
      const peerId = msg.from;
      const pc = ensurePeerConnection(peerId);
      await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      return;
    }
    if (msg.type === 'ice-candidate') {
      const peerId = msg.from;
      const pc = ensurePeerConnection(peerId);
      if (msg.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
        } catch (e) {
          // ignore
        }
      }
    }
  };

  const startLocalMedia = async () => {
    if (cameraStreamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      cameraStreamRef.current = stream;
      // Ensure initial UI state matches track state (especially when starting muted).
      try {
        for (const t of stream.getVideoTracks()) t.enabled = Boolean(isVideoOn);
        for (const t of stream.getAudioTracks()) t.enabled = Boolean(isAudioOn);
      } catch {
        // ignore
      }
      setLocalPreviewStream(stream);
      setMediaError(null);
    } catch (e) {
      cameraStreamRef.current = null;
      setLocalPreviewStream(null);
      const msg =
        (e as any)?.name === 'NotAllowedError'
          ? 'Permisiunile pentru camera/microfon sunt blocate. Activeaza-le din setarile telefonului.'
          : 'Nu am putut porni camera/microfonul pe acest dispozitiv.';
      setMediaError(msg);
    }
  };

  const ensureLocalAudioTrack = async (): Promise<MediaStreamTrack | null> => {
    const cam = cameraStreamRef.current;
    if (!cam) return null;
    const existing = cam.getAudioTracks?.()[0] ?? null;
    if (existing) return existing;

    try {
      const astream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const audioTrack = astream.getAudioTracks?.()[0] ?? null;
      if (!audioTrack) return null;

      cam.addTrack(audioTrack);

      // Attach/replace to existing peer connections.
      for (const pc of pcsRef.current.values()) {
        const sender =
          pc.getSenders().find((s) => s.track?.kind === 'audio') ||
          pc.getTransceivers().find((t) => t?.receiver?.track?.kind === 'audio')?.sender;
        if (sender) {
          try {
            await sender.replaceTrack(audioTrack);
          } catch {
            // ignore
          }
        } else {
          try {
            pc.addTrack(audioTrack, cam);
          } catch {
            // ignore
          }
        }
      }

      // If we're showing camera preview, keep it as the combined stream.
      setLocalPreviewStream(cam);
      return audioTrack;
    } catch (e) {
      try {
        const msg =
          (e as any)?.name === 'NotAllowedError'
            ? 'Permisiunea pentru microfon este blocata. Activeaza microfonul din setarile browserului/telefonului.'
            : 'Nu am putut porni microfonul pe acest dispozitiv.';
        setMediaError(msg);
      } catch {
        // ignore
      }
      return null;
    }
  };

  const stopLocalMedia = () => {
    const cam = cameraStreamRef.current;
    cameraStreamRef.current = null;
    if (cam) {
      for (const t of cam.getTracks()) t.stop();
    }
    const screen = screenStreamRef.current;
    screenStreamRef.current = null;
    if (screen) {
      for (const t of screen.getTracks()) t.stop();
    }
    setLocalPreviewStream(null);
  };

  // connect signaling WS
  useEffect(() => {
    if (!eventId || !me?.id) return;
    const token = getToken();
    if (!token) return;

    let cancelled = false;
    void startLocalMedia().finally(() => {
      if (cancelled) return;

      const url = wsUrl(`/ws/meetings/${eventId}`, { token });
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(String(ev.data || '{}'));
          void handleSignal(parsed as SignalMessage);
        } catch (e) {
          // ignore
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
      };
    });

    return () => {
      cancelled = true;
      try {
        wsRef.current?.close();
      } catch (e) {
        // ignore
      }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, me?.id]);

  // Enable playback (remote audio/video) + ensure mic can start after first user gesture (mobile autoplay + permission quirks).
  useEffect(() => {
    const onFirstGesture = () => {
      setPlaybackEnabled(true);
      if (isAudioOn) {
        const cam = cameraStreamRef.current;
        const audioTrack = cam?.getAudioTracks?.()?.[0] ?? null;
        if (!audioTrack || audioTrack.readyState === 'ended') {
          void ensureLocalAudioTrack().finally(() => setTrackEnabled('audio', true));
        } else {
          setTrackEnabled('audio', true);
        }
      }
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: true, capture: true });
    window.addEventListener('touchstart', onFirstGesture, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture, true);
      window.removeEventListener('touchstart', onFirstGesture, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAudioOn]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        wsRef.current?.close();
      } catch (e) {
        // ignore
      }
      wsRef.current = null;
      for (const peerId of Array.from(pcsRef.current.keys())) closePeer(peerId);
      pcsRef.current.clear();
      stopLocalMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTrackEnabled = (kind: 'audio' | 'video', enabled: boolean) => {
    if (kind === 'video') {
      const screen = screenStreamRef.current;
      if (isScreenSharing && screen) {
        for (const t of screen.getVideoTracks()) t.enabled = enabled;
        return;
      }
      const cam = cameraStreamRef.current;
      if (!cam) return;
      for (const t of cam.getVideoTracks()) t.enabled = enabled;
      return;
    }

    const cam = cameraStreamRef.current;
    if (!cam) return;
    for (const t of cam.getAudioTracks()) t.enabled = enabled;
  };

  const handleToggleVideo = (participantId?: string) => {
    if (!participantId || participantId !== String(me?.id ?? '')) return;
    setPlaybackEnabled(true);
    const next = !isVideoOn;
    setIsVideoOn(next);
    setTrackEnabled('video', next);
  };

  const handleToggleAudio = (participantId?: string) => {
    if (!participantId || participantId !== String(me?.id ?? '')) return;
    setPlaybackEnabled(true);
    const next = !isAudioOn;
    setIsAudioOn(next);
    if (next) {
      void ensureLocalAudioTrack().finally(() => setTrackEnabled('audio', true));
      return;
    }
    setTrackEnabled('audio', false);
  };

  const stopScreenShare = async () => {
    const screen = screenStreamRef.current;
    screenStreamRef.current = null;
    if (screen) {
      for (const t of screen.getTracks()) t.stop();
    }

    const cam = cameraStreamRef.current;
    if (cam) setLocalPreviewStream(cam);

    // restore camera track to all peers
    if (cam) {
      const camVideo = cam.getVideoTracks()[0];
      for (const pc of pcsRef.current.values()) {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender && camVideo) {
          try {
            await sender.replaceTrack(camVideo);
          } catch (e) {
            // ignore
          }
        }
      }
    }

    setIsScreenSharing(false);
  };

  const handleToggleScreenShare = async () => {
    if (!canModerate) return;
    setPlaybackEnabled(true);
    if (isScreenSharing) {
      void stopScreenShare();
      return;
    }

    try {
      const screen = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
      const screenTrack: MediaStreamTrack | undefined = screen?.getVideoTracks?.()[0];
      if (!screenTrack) return;

      screenStreamRef.current = screen;
      screenTrack.onended = () => void stopScreenShare();

      const cam = cameraStreamRef.current;
      const audioTrack = cam?.getAudioTracks?.()[0] ?? null;
      const combined = new MediaStream(audioTrack ? [screenTrack, audioTrack] : [screenTrack]);
      setLocalPreviewStream(combined);

      for (const pc of pcsRef.current.values()) {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) {
          try {
            await sender.replaceTrack(screenTrack);
          } catch (e) {
            // ignore
          }
        }
      }
      setIsScreenSharing(true);
    } catch (e) {
      // ignore
    }
  };

  const handleToggleRecording = () => {
    setPlaybackEnabled(true);
    setIsRecording((v) => !v);
  };

  const handleToggleHand = () => {
    setPlaybackEnabled(true);
    const next = !isHandRaised;
    setIsHandRaised(next);
    sendSignal({ type: 'hand', raised: next });
  };
  const handleLowerHand = (participantId: string) => {
    if (participantId === String(me?.id ?? '')) {
      setIsHandRaised(false);
      sendSignal({ type: 'hand', raised: false });
      return;
    }
    // Best-effort: we can reflect it locally for moderators, but there is no targeted WS command yet.
    setHandRaisedByUserId((prev) => ({ ...prev, [String(participantId)]: false }));
  };

  const handleVote = (optionId: string) => {
    if (!eventId || !activePoll) return;
    const pollId = Number(activePoll.id);
    const optId = Number(optionId);
    if (!Number.isFinite(pollId) || !Number.isFinite(optId)) return;
    voteMeetingPoll(eventId, pollId, optId)
      .then((p) => {
        setActivePoll(mapPoll(p));
        setHasVotedInPoll(Boolean(p.my_vote_option_id));
      })
      .catch(() => {});
  };

  const handleDownloadFile = (_fileId: string) => {};

  const title = meeting ? `Clasă online - ${meeting.title}` : 'Clasă online - Platforma Scola';

  if (!eventId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="Video" size={24} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-foreground">Clasă online</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Deschide sesiunea din Calendar (butonul “Join”) sau folosește linkul cu `?event=ID`.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/calendar')}
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Mergi la Calendar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Participă la clase virtuale cu videoconferință, partajare de ecran și instrumente interactive"
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          user={currentUser}
          notificationCount={0}
          onNavigate={handleNavigation}
          onSignOut={() => signOut()}
        />

        <DashboardSidebar user={currentUser} isCollapsed={isSidebarCollapsed} onNavigate={handleNavigation} />

        <main
          className={`pt-16 pb-20 transition-all duration-300 ${
            isSidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64'
          }`}
        >
          {session && <SessionInfo session={session} participantCount={uiParticipants.length} />}

          <div className="p-4 lg:p-6">
            {loading && (
              <div className="mb-4 text-sm text-muted-foreground">Se încarcă sesiunea…</div>
            )}
            {error && (
              <div className="mb-4 p-3 rounded-md bg-error/10 border border-error text-error text-sm">
                {error}
              </div>
            )}
            {mediaError && (
              <div className="mb-4 p-3 rounded-md bg-warning/10 border border-warning text-foreground text-sm">
                {mediaError}
              </div>
            )}
            {meeting && (
              <div className="mb-4 text-xs text-muted-foreground">
                Sesiune: <span className="text-foreground font-medium">{meeting.title}</span> •{' '}
                {fmtDateTimeRo(new Date(meeting.start_time))} – {fmtDateTimeRo(new Date(meeting.end_time))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-card border border-border rounded-lg p-4 h-[calc(100vh-280px)]">
                  <VideoGrid
                    participants={uiParticipants}
                    mainPresenter={mainPresenter}
                    onToggleVideo={(id) => handleToggleVideo(id)}
                    onToggleAudio={(id) => handleToggleAudio(id)}
                    currentUserId={String(me?.id ?? '')}
                    playbackEnabled={playbackEnabled}
                  />
                </div>

                {isScreenSharing && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Monitor" size={20} className="text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Partajare ecran</h3>
                    </div>
                    <div className="aspect-video bg-slate-900 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Monitor" size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">Partajarea ecranului este activă</p>
                      </div>
                    </div>
                  </div>
                )}

                {!isSidebarCollapsed && isChatOpen && (
                  <div
                    ref={chatInlineRef}
                    className="bg-card border border-border rounded-lg h-[52vh] min-h-[340px] max-h-[680px]"
                  >
                    <ChatPanel
                      messages={messages}
                      currentUserId={String(me?.id ?? '')}
                      currentUserRole={currentUser.role}
                      allowPrivate={false}
                      onSendMessage={(m) => handleSendMessage(m)}
                      isOpen={true}
                      onClose={() => setIsChatOpen(false)}
                      mode="inline"
                      className="border-0 shadow-none rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <PollWidget
                  poll={activePoll}
                  currentUserRole={canModerate ? 'teacher' : 'student'}
                  hasVoted={hasVotedInPoll}
                  onVote={handleVote}
                  onCreatePoll={async () => {
                    if (!eventId) return;
                    const q = window.prompt('Întrebare sondaj:');
                    if (!q) return;
                    const optsRaw = window.prompt('Opțiuni (separate prin virgulă sau linie nouă):', 'Da,Nu');
                    if (!optsRaw) return;
                    const opts = optsRaw
                      .split(/\r?\n|,/g)
                      .map((s) => s.trim())
                      .filter(Boolean);
                    try {
                      const p = await createMeetingPoll(eventId, { question: q, options: opts });
                      setActivePoll(mapPoll(p));
                      setHasVotedInPoll(Boolean(p.my_vote_option_id));
                    } catch (e: any) {
                      window.alert(e?.response?.data?.detail || 'Nu am putut crea sondajul.');
                    }
                  }}
                  onClosePoll={async () => {
                    if (!eventId || !activePoll) return;
                    const ok = window.confirm('Închizi sondajul?');
                    if (!ok) return;
                    try {
                      await closeMeetingPoll(eventId, Number(activePoll.id));
                      await refreshPoll();
                    } catch (e: any) {
                      window.alert(e?.response?.data?.detail || 'Nu am putut închide sondajul.');
                    }
                  }}
                />

                <SharedFiles
                  files={sharedFiles}
                  currentUserRole={canModerate ? 'teacher' : 'student'}
                  onUploadFile={() => fileInputRef.current?.click()}
                  onDownloadFile={(fileId) => {
                    const f = sharedFiles.find((x) => x.id === fileId);
                    if (!f?.url) return;
                    const isNative =
                      Boolean((window as any)?.Capacitor?.isNativePlatform?.()) ||
                      String(window?.location?.protocol || '') === 'capacitor:' ||
                      String(window?.location?.protocol || '') === 'file:';
                    const webBase = String(import.meta.env.VITE_API_BASE_URL_WEB || '').trim().replace(/\/$/, '');
                    const nativeBase = String(import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '');
                    const baseURL = isNative
                      ? nativeBase || webBase || 'http://10.0.2.2:8000'
                      : webBase || window.location.origin;
                    const abs = f.url.startsWith('http') ? f.url : `${baseURL}${f.url}`;
                    window.open(abs, '_blank', 'noopener,noreferrer');
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0] || null;
                    e.target.value = '';
                    if (!f || !eventId) return;
                    setUploadBusy(true);
                    try {
                      await uploadMeetingFile(eventId, f);
                      await refreshFiles();
                    } catch (err: any) {
                      window.alert(err?.response?.data?.detail || 'Nu am putut încărca fișierul.');
                    } finally {
                      setUploadBusy(false);
                    }
                  }}
                />
                {filesError && (
                  <div className="text-sm text-error bg-error/10 border border-error/20 rounded-md p-3">
                    {filesError}
                  </div>
                )}
                {uploadBusy && (
                  <div className="text-xs text-muted-foreground">Se încarcă fișierul…</div>
                )}

                {currentUser.role === 'parent' && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="UserPlus" size={18} className="text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Adaugă copil</h3>
                      </div>
                    </div>

                    {myChildrenError && (
                      <div className="mb-3 text-sm text-error bg-error/10 border border-error/20 rounded-md p-2">
                        {myChildrenError}
                      </div>
                    )}
                    {addChildInfo && (
                      <div className="mb-3 text-sm text-success bg-success/10 border border-success/20 rounded-md p-2">
                        {addChildInfo}
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <div className="text-sm font-medium text-foreground mb-2">Copil</div>
                        <select
                          className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          value={selectedChildId == null ? '' : String(selectedChildId)}
                          onChange={(e) => setSelectedChildId(e.target.value ? Number(e.target.value) : null)}
                          disabled={myChildrenLoading || addChildBusy || myChildren.length === 0}
                        >
                          {myChildren.length === 0 ? (
                            <option value="">Nu există copii asociați</option>
                          ) : (
                            myChildren.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.full_name}
                                {c.classroom_name ? ` (${c.classroom_name})` : ''}
                              </option>
                            ))
                          )}
                        </select>
                        <div className="text-xs text-muted-foreground mt-2">
                          Copilul va primi o invitație în Calendar și va putea intra după ce apasă “Acceptă”.
                        </div>
                      </div>

                      <button
                        className="h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={addChildBusy || myChildrenLoading || selectedChildId == null}
                        onClick={async () => {
                          if (!eventId || selectedChildId == null) return;
                          setAddChildBusy(true);
                          setAddChildInfo(null);
                          try {
                            const resp = await addChildToMeeting(eventId, selectedChildId);
                            if (resp.added) {
                              setAddChildInfo('Invitația a fost trimisă copilului.');
                              const m = await fetchMeeting(eventId);
                              setMeeting(m);
                            } else {
                              setAddChildInfo('Copilul era deja invitat la această sesiune.');
                            }
                          } catch (e: any) {
                            setAddChildInfo(null);
                            setMyChildrenError(e?.response?.data?.detail || 'Nu am putut adăuga copilul.');
                          } finally {
                            setAddChildBusy(false);
                          }
                        }}
                      >
                        {addChildBusy ? 'Se trimite…' : 'Trimite invitația'}
                      </button>
                    </div>
                  </div>
                )}

                {!isSidebarCollapsed && (
                  <div ref={participantsCardRef}>
                    <ParticipantsCard participants={uiParticipants} currentUserRole={currentUser.role} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        {isSidebarCollapsed && (
          <ChatPanel
            messages={messages}
            currentUserId={String(me?.id ?? '')}
            currentUserRole={currentUser.role}
            allowPrivate={false}
            onSendMessage={(m) => handleSendMessage(m)}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            mode="drawer"
          />
        )}

        {isSidebarCollapsed && (
          <ParticipantsList
            participants={uiParticipants}
            currentUserRole={currentUser.role}
            onToggleAudio={(id) => handleToggleAudio(id)}
            onLowerHand={handleLowerHand}
            onRemoveParticipant={undefined}
            isOpen={isParticipantsOpen}
            onClose={() => setIsParticipantsOpen(false)}
          />
        )}

        <ControlBar
          isVideoOn={isVideoOn}
          isAudioOn={isAudioOn}
          isScreenSharing={isScreenSharing}
          isRecording={isRecording}
          isHandRaised={isHandRaised}
          currentUserRole={currentUser.role}
          screenShareAvailable={screenShareAvailable}
          recordingAvailable={recordingAvailable}
          settingsAvailable={settingsAvailable}
          onToggleVideo={() => handleToggleVideo(String(me?.id ?? ''))}
          onToggleAudio={() => handleToggleAudio(String(me?.id ?? ''))}
          onToggleScreenShare={handleToggleScreenShare}
          onToggleRecording={handleToggleRecording}
          onToggleHand={handleToggleHand}
          onLeaveClass={handleLeaveClass}
          onOpenChat={() => openChatInline()}
          onOpenParticipants={() => {
            setPlaybackEnabled(true);
            if (isSidebarCollapsed) setIsParticipantsOpen(true);
            else participantsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onOpenSettings={() => {}}
        />

        <SubscriptionRequiredModal open={upgradeOpen} feature="messages" onClose={() => setUpgradeOpen(false)} />
      </div>
    </>
  );
};

export default OnlineClassroom;
